import { useCallback } from 'react';
import type { Editor } from '@tiptap/react';

import API from 'utils/api';
import { isProxyUrl } from 'containers/Tiptap/lib/tiptap-utils';
import { HEADER_FOOTER_IMG_SELECTOR, getHeaderFooterRoot } from 'containers/Tiptap/lib/header-footer-dom';
import { useHeaderFooterDomPatch } from 'containers/Tiptap/hooks/use-header-footer-dom-patch';

// Swap only the displayed src; the proxy URL stays canonical for persistence and export.
const resolved = new Map<string, string>();
const pending = new Set<string>();

const cacheKey = (proxy: string, shareId?: string): string => `${shareId ?? ''}:${proxy}`;

export function useResolveHeaderFooterImages(editor: Editor | null, shareId?: string): void {
  const apply = useCallback((): void => {
    if (!editor) return;
    const root = getHeaderFooterRoot(editor);
    root.querySelectorAll<HTMLImageElement>(HEADER_FOOTER_IMG_SELECTOR).forEach((img) => {
      const proxy = img.getAttribute('src') ?? '';
      if (!proxy || !isProxyUrl(proxy)) return;

      const key = cacheKey(proxy, shareId);
      const hit = resolved.get(key);
      if (hit) {
        if (img.src !== hit) img.src = hit;
        return;
      }
      if (pending.has(key)) return;
      pending.add(key);
      API.common
        .resolveFileUrl(proxy, shareId)()
        .then((res) => {
          resolved.set(key, res.data.url);
          getHeaderFooterRoot(editor).querySelectorAll<HTMLImageElement>(HEADER_FOOTER_IMG_SELECTOR).forEach((el) => {
            if (el.getAttribute('src') === proxy) el.src = res.data.url;
          });
        })
        .catch(() => {})
        .finally(() => pending.delete(key));
    });
  }, [editor, shareId]);

  useHeaderFooterDomPatch(editor, apply);
}
